import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, feedbackAPI, projectAPI, volunteerAPI, issuesAPI } from '../services/api';
import { Feedback, Project } from '../types';

interface VolunteerApplication {
  id: string;
  full_name: string;
  email: string;
  phone_number: string;
  ward_name: string;
  education_level: string;
  occupation: string;
  motivation: string;
  availability_hours_per_week: number;
  skills_and_expertise: string;
  previous_volunteer_experience: string;
  status: string;
  reviewer_name?: string;
  review_date?: string;
  review_notes?: string;
  created_at: string;
}

const WardOfficerPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ward-feedback');
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [volunteerApplications, setVolunteerApplications] = useState<VolunteerApplication[]>([]);
  const [pendingEngagements, setPendingEngagements] = useState<any[]>([]);
  const [wardEngagements, setWardEngagements] = useState<any[]>([]);
  const [wardIssues, setWardIssues] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedProject, setSelectedProject] = useState<string>('');

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const hasAccess = user && user.role === 'ward_officer';

  useEffect(() => {
    if (hasAccess) {
      loadData();
    }
  }, [activeTab, hasAccess]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'ward-feedback') {
        const feedbackRes = await feedbackAPI.getAdmin();
        setFeedback(feedbackRes.data.filter((f: Feedback) => f.ward === user.ward_id));
      } else if (activeTab === 'project-progress' || activeTab === 'field-evidence') {
        // Check if user has ward_id, if not, show error
        if (!user.ward_id) {
          setMessage('Ward ID not found in user profile. Please contact administrator.');
          console.error('User ward_id is missing:', user);
          return;
        }
        const projectsRes = await projectAPI.getByWard(user.ward_id);
        setProjects(projectsRes.data);
      } else if (activeTab === 'volunteer-management') {
        const applicationsRes = await volunteerAPI.getApplications(user.ward_id, 'pending');
        setVolunteerApplications(applicationsRes.data);
      } else if (activeTab === 'volunteer-approvals') {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_BASE}/ward/engagements/pending?ward_id=${user.ward_id}`);
        const data = await response.json();
        setPendingEngagements(Array.isArray(data) ? data : []);
      } else if (activeTab === 'volunteer-activities') {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_BASE}/engagements?ward_id=${user.ward_id}`);
        const data = await response.json();
        setWardEngagements(Array.isArray(data) ? data : []);
      } else if (activeTab === 'flag-issues') {
        const issuesRes = await issuesAPI.getAll(undefined, user.ward_id);
        setWardIssues(issuesRes.data || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      setMessage('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const respondToFeedback = async (feedbackId: string, response: string) => {
    try {
      await feedbackAPI.respond(feedbackId, { response, responder_role: 'ward_officer' });
      setMessage('Response submitted successfully');
      loadData();
    } catch (error) {
      setMessage('Failed to submit response');
    }
  };

  const uploadEvidence = async (projectId: string, files: FileList) => {
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => formData.append('evidence', file));
      
      await adminAPI.uploadEvidence(projectId, formData);
      setMessage('Field evidence uploaded successfully');
      loadData();
    } catch (error) {
      setMessage('Failed to upload evidence');
    }
  };

  const updateProjectProgress = async (projectId: string, progress: number, notes: string) => {
    try {
      await projectAPI.updateProgress(projectId, { progress, notes, updated_by: user.id });
      setMessage('Project progress updated successfully');
      loadData();
    } catch (error) {
      setMessage('Failed to update project progress');
    }
  };

  const flagIssue = async (issueData: any) => {
    try {
      await issuesAPI.create({
        ...issueData,
        ward_id: user.ward_id || null,
        flagged_by: user.id
      });
      setMessage('Issue flagged to institutional responder successfully');
    } catch (error) {
      setMessage('Failed to flag issue');
    }
  };

  const reviewVolunteerApplication = async (applicationId: string, action: 'approve' | 'reject', notes: string) => {
    try {
      await volunteerAPI.reviewApplication(applicationId, {
        action,
        notes,
        reviewed_by: user.id
      });
      setMessage(`Volunteer application ${action}d successfully`);
      loadData(); // Reload applications
    } catch (error) {
      setMessage(`Failed to ${action} application`);
    }
  };

  const approveVolunteerEngagement = async (engagementId: string, action: 'approve' | 'reject', reason?: string) => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE}/ward/engagements/${engagementId}/approve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
        },
        body: JSON.stringify({
          action,
          approved_by: user.id,
          reason: reason || ''
        })
      });
      
      if (response.ok) {
        setMessage(`Volunteer engagement ${action}d successfully`);
        loadData();
      } else {
        setMessage(`Failed to ${action} engagement`);
      }
    } catch (error) {
      console.error('Approval error:', error);
      setMessage(`Failed to ${action} engagement`);
    }
  };

  if (!hasAccess) {
    return (
      <div className="ward-officer-page">
        <header className="page-header">
          <div className="icon icon-primary">🔒</div>
          <h1>Civic Moderator Access Required</h1>
          <p>Only Civic Moderators can access this page</p>
        </header>
        <div className="access-denied">
          <p>This page is restricted to Civic Moderators only.</p>
          <button onClick={() => navigate('/login')} className="btn btn-primary">Staff Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ward-officer-page">
      <header className="page-header">
        <div className="icon icon-primary">🏘️</div>
        <h1>Civic Moderator Dashboard</h1>
        <p>Handle citizen engagement and verification at ward level</p>
      </header>

      {message && (
        <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="ward-officer-tabs">
        <div className="tabs-container">
          <button 
            className={`tab-button ${activeTab === 'ward-feedback' ? 'active' : ''}`}
            onClick={() => setActiveTab('ward-feedback')}
          >
            📋 Ward Feedback
          </button>
          <button 
            className={`tab-button ${activeTab === 'citizen-questions' ? 'active' : ''}`}
            onClick={() => setActiveTab('citizen-questions')}
          >
            💬 Citizen Questions
          </button>
          <button 
            className={`tab-button ${activeTab === 'field-evidence' ? 'active' : ''}`}
            onClick={() => setActiveTab('field-evidence')}
          >
            📸 Field Evidence
          </button>
          <button 
            className={`tab-button ${activeTab === 'project-progress' ? 'active' : ''}`}
            onClick={() => setActiveTab('project-progress')}
          >
            📈 Project Progress
          </button>
          <button 
            className={`tab-button ${activeTab === 'flag-issues' ? 'active' : ''}`}
            onClick={() => setActiveTab('flag-issues')}
          >
            🚩 Flag Issues
          </button>
          <button 
            className={`tab-button ${activeTab === 'community-engagement' ? 'active' : ''}`}
            onClick={() => setActiveTab('community-engagement')}
          >
            🤝 Community Engagement
          </button>
          <button 
            className={`tab-button ${activeTab === 'volunteer-management' ? 'active' : ''}`}
            onClick={() => setActiveTab('volunteer-management')}
          >
            👥 Volunteer Management
          </button>
          <button 
            className={`tab-button ${activeTab === 'volunteer-activities' ? 'active' : ''}`}
            onClick={() => setActiveTab('volunteer-activities')}
          >
            📋 Volunteer Activities
          </button>
          <button 
            className={`tab-button ${activeTab === 'volunteer-approvals' ? 'active' : ''}`}
            onClick={() => setActiveTab('volunteer-approvals')}
          >
            ✅ Volunteer Approvals
          </button>
        </div>
      </div>

      <div className="ward-officer-content">
        {activeTab === 'ward-feedback' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">📋</div>
              <div>
                <h2>Monitor Ward Feedback</h2>
                <p>Monitor citizen feedback within your ward and track community concerns.</p>
              </div>
            </div>
            
            {loading ? (
              <div className="loading">Loading ward feedback...</div>
            ) : (
              <div className="feedback-list">
                {feedback.length === 0 ? (
                  <div className="empty-state">
                    <p>No feedback from your ward at this time</p>
                  </div>
                ) : (
                  feedback.map(item => (
                    <div key={item.id} className="feedback-item">
                      <div className="feedback-header">
                        <div className="feedback-info">
                          <strong>{item.is_anonymous ? 'Anonymous Citizen' : item.name}</strong>
                          <span className="feedback-type">{item.feedback_type}</span>
                          <span className={`status-badge ${item.status}`}>
                            {item.status}
                          </span>
                        </div>
                        <div className="feedback-date">
                          {new Date(item.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      
                      <div className="feedback-details">
                        <p><strong>Message:</strong> {item.message}</p>
                        {item.project_reference && <p><strong>Project Reference:</strong> {item.project_reference}</p>}
                      </div>

                      <div className="feedback-actions">
                        <button 
                          onClick={() => {
                            const response = prompt('Enter your response to this citizen:');
                            if (response) respondToFeedback(item.id, response);
                          }}
                          className="respond-btn"
                        >
                          Respond to Citizen
                        </button>
                        <button 
                          onClick={() => setActiveTab('field-evidence')}
                          className="investigate-btn"
                        >
                          Investigate & Document
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        )}

        {activeTab === 'citizen-questions' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">💬</div>
              <div>
                <h2>Respond to Citizen Questions</h2>
                <p>Respond to feedback submitted by citizens in your ward.</p>
              </div>
            </div>
            {loading ? <div className="loading">Loading...</div> : (
              <div className="feedback-list">
                {feedback.length === 0 ? (
                  <div className="empty-state"><p>No citizen feedback in your ward.</p></div>
                ) : (
                  feedback.map(item => (
                    <div key={item.id} className="feedback-item">
                      <div className="feedback-header">
                        <div className="feedback-info">
                          <strong>{item.is_anonymous ? 'Anonymous Citizen' : item.name}</strong>
                          <span className="feedback-type">{item.feedback_type}</span>
                          <span className={`status-badge ${item.status}`}>{item.status}</span>
                        </div>
                        <div className="feedback-date">{new Date(item.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="feedback-details">
                        <p><strong>Message:</strong> {item.message}</p>
                      </div>
                      <div className="feedback-actions">
                        <button onClick={() => {
                          const response = prompt('Enter your response:');
                          if (response) respondToFeedback(item.id, response);
                        }} className="respond-btn">Reply to Citizen</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        )}

        {activeTab === 'field-evidence' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">📸</div>
              <div>
                <h2>Upload Field Evidence</h2>
                <p>Upload photos and videos from the field to verify project status and address citizen concerns.</p>
              </div>
            </div>
            
            <div className="evidence-upload-form">
              <h3>Upload Evidence</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const projectId = formData.get('project_id') as string;
                const fileInput = (e.target as HTMLFormElement).querySelector('input[type="file"]') as HTMLInputElement;
                const files = fileInput?.files;
                
                if (projectId && files) {
                  uploadEvidence(projectId, files);
                }
              }}>
                <div className="form-row">
                  <select name="project_id" required>
                    <option value="">Select Project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <input 
                    name="evidence" 
                    type="file" 
                    multiple 
                    accept="image/*,video/*,application/pdf" 
                    required 
                  />
                  <small>Upload photos, videos, or PDF documents</small>
                </div>
                <div className="form-row">
                  <textarea 
                    name="description" 
                    placeholder="Description of evidence (what does this show?)" 
                    rows={3}
                    required
                  />
                </div>
                <button type="submit">Upload Field Evidence</button>
              </form>
            </div>

            <div className="evidence-guidelines">
              <h3>Evidence Guidelines</h3>
              <ul>
                <li>Take clear, well-lit photos showing project progress</li>
                <li>Include date and location information</li>
                <li>Document both progress and any issues found</li>
                <li>Provide context in descriptions</li>
              </ul>
            </div>
          </section>
        )}

        {activeTab === 'project-progress' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">📈</div>
              <div>
                <h2>Update Project Progress</h2>
                <p>Provide regular updates on project progress based on field observations.</p>
              </div>
            </div>
            
            {loading ? (
              <div className="loading">Loading ward projects...</div>
            ) : (
              <div className="projects-list">
                {projects.length === 0 ? (
                  <div className="empty-state">
                    <p>No projects in your ward at this time</p>
                  </div>
                ) : (
                  projects.map(project => (
                    <div key={project.id} className="project-item">
                      <div className="project-header">
                        <h3>{project.name}</h3>
                        <span className="project-budget">KES {project.budget.toLocaleString()}</span>
                      </div>
                      <div className="project-details">
                        <p><strong>Current Progress:</strong> {project.progress}%</p>
                        <p><strong>Status:</strong> {project.status}</p>
                        <p><strong>Expected Completion:</strong> {project.expected_completion}</p>
                      </div>
                      <div className="progress-update-form">
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          const formData = new FormData(e.target as HTMLFormElement);
                          const progress = parseInt(formData.get('progress') as string);
                          const notes = formData.get('notes') as string;
                          updateProjectProgress(project.id, progress, notes);
                        }}>
                          <div className="form-row">
                            <input 
                              name="progress" 
                              type="number" 
                              min="0" 
                              max="100" 
                              placeholder="Progress %" 
                              required 
                            />
                            <select name="status">
                              <option value="planned">Planned</option>
                              <option value="procurement">Procurement</option>
                              <option value="ongoing">Ongoing</option>
                              <option value="completed">Completed</option>
                              <option value="stalled">Stalled</option>
                            </select>
                          </div>
                          <div className="form-row">
                            <textarea 
                              name="notes" 
                              placeholder="Progress notes and observations" 
                              rows={2}
                              required
                            />
                          </div>
                          <button type="submit">Update Progress</button>
                        </form>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        )}

        {activeTab === 'flag-issues' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">🚩</div>
              <div>
                <h2>Flag Serious Issues</h2>
                <p>Flag serious issues to institutional responders for immediate attention and resolution.</p>
              </div>
            </div>
            
            <div className="flag-issue-form">
              <h3>Report Serious Issue</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const issueData = {
                  title: formData.get('title'),
                  description: formData.get('description'),
                  issue_type: formData.get('issue_type'),
                  project_id: formData.get('project_id') || null,
                  urgency: formData.get('urgency')
                };
                flagIssue(issueData);
              }}>
                <div className="form-row">
                  <input name="title" placeholder="Issue Title" required />
                  <select name="issue_type" required>
                    <option value="">Select Issue Type</option>
                    <option value="project_delay">Project Delay</option>
                    <option value="budget_misuse">Budget Misuse</option>
                    <option value="contractor_issue">Contractor Issue</option>
                    <option value="safety_concern">Safety Concern</option>
                    <option value="community_complaint">Community Complaint</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-row">
                  <select name="project_id">
                    <option value="">Related Project (optional)</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                  <select name="urgency" required>
                    <option value="high">High Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="low">Low Priority</option>
                  </select>
                </div>
                <div className="form-row">
                  <textarea 
                    name="description" 
                    placeholder="Detailed description of the issue" 
                    rows={4}
                    required
                  />
                </div>
                <button type="submit">Flag Issue to Institutional Responder</button>
              </form>
            </div>

            <div className="flagged-issues">
              <h3>Previously Flagged Issues</h3>
              {loading ? <div className="loading">Loading...</div> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', marginTop: '1rem' }}>
                  {wardIssues.length === 0
                    ? <p style={{ color: '#666' }}>No issues flagged yet.</p>
                    : wardIssues.map((issue: any) => (
                      <div key={issue.id} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1rem', borderLeft: `4px solid ${issue.urgency === 'high' ? '#e53935' : '#fb8c00'}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <strong>{issue.title}</strong>
                          <span style={{ fontSize: '0.8rem', color: issue.status === 'resolved' ? '#43a047' : '#fb8c00' }}>{issue.status.toUpperCase()}</span>
                        </div>
                        <p style={{ margin: '0.4rem 0 0', fontSize: '0.9rem' }}>{issue.description}</p>
                        {issue.response && <p style={{ margin: '0.4rem 0 0', fontSize: '0.85rem', color: '#43a047' }}><strong>Response:</strong> {issue.response}</p>}
                        <small style={{ color: '#999' }}>{new Date(issue.created_at).toLocaleDateString()}</small>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'community-engagement' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">🤝</div>
              <div>
                <h2>Ward Community Engagement</h2>
                <p>Document local community interactions, meetings, and field activities with multimedia evidence.</p>
              </div>
            </div>
            
            <div className="engagement-form">
              <h3>Record Ward Engagement</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                
                // Add user information
                formData.append('conducted_by', user?.id || '');
                formData.append('officer_role', 'ward_officer');
                formData.append('ward_id', user?.ward_id || '');
                
                // Submit engagement
                fetch('/api/admin/engagements', {
                  method: 'POST',
                  body: formData
                })
                .then(response => response.json())
                .then(data => {
                  if (data.message) {
                    setMessage('Ward engagement documented successfully');
                    (e.target as HTMLFormElement).reset();
                  } else {
                    setMessage('Failed to document engagement');
                  }
                })
                .catch(error => {
                  console.error('Engagement error:', error);
                  setMessage('Failed to document engagement');
                });
              }}>
                <div className="form-row">
                  <input name="title" placeholder="Engagement Title" required />
                  <select name="engagement_type" required>
                    <option value="">Select Engagement Type</option>
                    <option value="community_meeting">Community Meeting</option>
                    <option value="site_visit">Project Site Visit</option>
                    <option value="training_session">Community Training</option>
                    <option value="feedback_collection">Feedback Collection</option>
                    <option value="awareness_campaign">Awareness Campaign</option>
                    <option value="consultation">Community Consultation</option>
                  </select>
                </div>
                
                <div className="form-row">
                  <input name="location" placeholder="Specific Location in Ward" required />
                  <input name="participants_count" type="number" placeholder="Number of Participants" min="0" />
                </div>
                
                <div className="form-row">
                  <input name="engagement_date" type="datetime-local" required />
                  <input name="duration_hours" type="number" step="0.5" placeholder="Duration (hours)" min="0" />
                </div>
                
                <div className="form-row">
                  <select name="project_id">
                    <option value="">Related Project (optional)</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-row">
                  <textarea name="description" placeholder="Engagement Description and Activities" rows={3} required />
                </div>
                
                <div className="form-row">
                  <textarea name="outcomes" placeholder="Key Outcomes and Community Feedback" rows={2} />
                </div>
                
                <div className="form-row">
                  <textarea name="challenges" placeholder="Challenges or Issues Raised" rows={2} />
                </div>
                
                <div className="form-row">
                  <textarea name="recommendations" placeholder="Follow-up Actions and Recommendations" rows={2} />
                </div>
                
                <div className="file-upload-section">
                  <h4>Upload Evidence and Materials</h4>
                  <div className="upload-types">
                    <div className="upload-type">
                      <label>📷 Team Photos (With community members, at project sites)</label>
                      <input name="attachments" type="file" multiple accept="image/*" />
                    </div>
                    <div className="upload-type">
                      <label>🎥 Testimonial Videos (Community feedback, progress updates)</label>
                      <input name="attachments" type="file" multiple accept="video/*" />
                    </div>
                    <div className="upload-type">
                      <label>📄 Learning Materials (Handouts, guides, information sheets)</label>
                      <input name="attachments" type="file" multiple accept=".pdf,.doc,.docx,.ppt,.pptx" />
                    </div>
                    <div className="upload-type">
                      <label>🎤 Audio Recordings (Community discussions, testimonials)</label>
                      <input name="attachments" type="file" multiple accept="audio/*" />
                    </div>
                  </div>
                  <small>Maximum file size: 50MB per file. Upload multiple files to document the engagement comprehensively.</small>
                </div>
                
                <button type="submit" className="btn btn-primary">Document Ward Engagement</button>
              </form>
            </div>
            
            <div className="engagement-guidelines">
              <h3>Ward Engagement Best Practices</h3>
              <div className="guidelines-grid">
                <div className="guideline-item">
                  <h4>📷 Photo Documentation</h4>
                  <ul>
                    <li>Take group photos showing community participation</li>
                    <li>Document project progress with before/after shots</li>
                    <li>Capture key moments during discussions</li>
                    <li>Include photos of materials distributed</li>
                  </ul>
                </div>
                <div className="guideline-item">
                  <h4>🎥 Video Testimonials</h4>
                  <ul>
                    <li>Record community members sharing feedback</li>
                    <li>Document project impact stories</li>
                    <li>Capture training sessions or demonstrations</li>
                    <li>Record key announcements or updates</li>
                  </ul>
                </div>
                <div className="guideline-item">
                  <h4>📄 Learning Materials</h4>
                  <ul>
                    <li>Share project information sheets</li>
                    <li>Distribute budget summaries</li>
                    <li>Provide training handouts</li>
                    <li>Upload presentation materials used</li>
                  </ul>
                </div>
                <div className="guideline-item">
                  <h4>🎤 Audio Documentation</h4>
                  <ul>
                    <li>Record community discussions</li>
                    <li>Capture verbal feedback and concerns</li>
                    <li>Document testimonials from beneficiaries</li>
                    <li>Record key decisions or agreements</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'volunteer-management' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">👥</div>
              <div>
                <h2>Community Volunteer Management</h2>
                <p>Review and approve community volunteer applications for your ward.</p>
              </div>
            </div>
            
            {loading ? (
              <div className="loading">Loading volunteer applications...</div>
            ) : (
              <div className="volunteer-applications">
                {volunteerApplications.length === 0 ? (
                  <div className="empty-state">
                    <p>No pending volunteer applications for your ward</p>
                  </div>
                ) : (
                  <div className="applications-list">
                    {volunteerApplications.map(application => (
                      <div key={application.id} className="application-item">
                        <div className="application-header">
                          <div className="applicant-info">
                            <h3>{application.full_name}</h3>
                            <p>{application.email} | {application.phone_number}</p>
                          </div>
                          <div className="application-status">
                            <span className={`status-badge ${application.status}`}>
                              {application.status}
                            </span>
                            <small>{new Date(application.created_at).toLocaleDateString()}</small>
                          </div>
                        </div>
                        
                        <div className="application-details">
                          <div className="detail-row">
                            <strong>Education:</strong> {application.education_level}
                            <strong>Occupation:</strong> {application.occupation}
                          </div>
                          <div className="detail-row">
                            <strong>Availability:</strong> {application.availability_hours_per_week} hours/week
                          </div>
                          
                          <div className="motivation-section">
                            <strong>Motivation:</strong>
                            <p>{application.motivation}</p>
                          </div>
                          
                          {application.skills_and_expertise && (
                            <div className="skills-section">
                              <strong>Skills & Expertise:</strong>
                              <p>{application.skills_and_expertise}</p>
                            </div>
                          )}
                          
                          {application.previous_volunteer_experience && (
                            <div className="experience-section">
                              <strong>Previous Experience:</strong>
                              <p>{application.previous_volunteer_experience}</p>
                            </div>
                          )}
                        </div>
                        
                        {application.status === 'pending' && (
                          <div className="application-actions">
                            <button 
                              onClick={() => {
                                const notes = prompt('Enter approval notes (optional):');
                                reviewVolunteerApplication(application.id, 'approve', notes || '');
                              }}
                              className="btn btn-success"
                            >
                              ✓ Approve Application
                            </button>
                            <button 
                              onClick={() => {
                                const reason = prompt('Enter rejection reason:');
                                if (reason) {
                                  reviewVolunteerApplication(application.id, 'reject', reason);
                                }
                              }}
                              className="btn btn-danger"
                            >
                              ✗ Reject Application
                            </button>
                          </div>
                        )}
                        
                        {application.review_notes && (
                          <div className="review-notes">
                            <strong>Review Notes:</strong>
                            <p>{application.review_notes}</p>
                            {application.reviewer_name && (
                              <small>Reviewed by {application.reviewer_name} on {new Date(application.review_date!).toLocaleDateString()}</small>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div className="volunteer-info">
              <h3>About Community Volunteers</h3>
              <p>Community volunteers help extend the reach of ward services by:</p>
              <ul>
                <li>📚 Sharing educational materials and resources with citizens</li>
                <li>🤝 Documenting community engagements and activities</li>
                <li>📸 Capturing community feedback through photos and videos</li>
                <li>🔄 Facilitating communication between citizens and civic moderators</li>
              </ul>
              
              <h4>Approval Guidelines</h4>
              <ul>
                <li>Verify applicant's residence in the ward</li>
                <li>Assess commitment level and availability</li>
                <li>Consider relevant skills and experience</li>
                <li>Ensure clear motivation for community service</li>
              </ul>
            </div>
          </section>
        )}

        {activeTab === 'volunteer-activities' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">📋</div>
              <div>
                <h2>Ward Volunteer Activities</h2>
                <p>Approved community volunteer engagements in your ward.</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <div className="stat-card" style={{ flex: 1, minWidth: '130px' }}>
                <h3>{wardEngagements.length}</h3><p>Total Activities</p>
              </div>
              <div className="stat-card" style={{ flex: 1, minWidth: '130px' }}>
                <h3>{new Set(wardEngagements.map((e: any) => e.officer_name)).size}</h3><p>Active Volunteers</p>
              </div>
              <div className="stat-card" style={{ flex: 1, minWidth: '130px' }}>
                <h3>{wardEngagements.reduce((s: number, e: any) => s + (e.participants_count || 0), 0)}</h3><p>People Reached</p>
              </div>
              <div className="stat-card" style={{ flex: 1, minWidth: '130px' }}>
                <h3>{wardEngagements.reduce((s: number, e: any) => s + (e.duration_hours || 0), 0)}h</h3><p>Hours Logged</p>
              </div>
            </div>
            {loading ? <div className="loading">Loading activities...</div> : (
              wardEngagements.length === 0 ? (
                <div className="empty-state"><p>No approved volunteer activities in your ward yet.</p></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {wardEngagements.map((eng: any) => (
                    <div key={eng.id} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1.2rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div>
                          <h3 style={{ margin: 0 }}>{eng.title}</h3>
                          <small style={{ color: '#666' }}>
                            {eng.engagement_type?.replace(/_/g, ' ')} &bull; {eng.officer_name} &bull; {new Date(eng.engagement_date).toLocaleDateString()}
                          </small>
                        </div>
                        <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '0.8rem', background: '#e8f5e9', color: '#43a047', fontWeight: 600 }}>✅ Approved</span>
                      </div>
                      <p style={{ margin: '0.5rem 0 0', color: '#444', fontSize: '0.9rem' }}>{eng.description}</p>
                      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.85rem', color: '#666', flexWrap: 'wrap' }}>
                        <span>📍 {eng.location}</span>
                        <span>👥 {eng.participants_count} participants</span>
                        <span>⏱️ {eng.duration_hours}h</span>
                        {eng.outcomes && <span>🎯 {eng.outcomes}</span>}
                      </div>
                      {eng.attachments?.length > 0 && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                          <strong>📎 Attachments:</strong> {eng.attachments.map((a: any) => (
                            <a key={a.id} href={`http://localhost:5000${a.url}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '0.5rem' }}>
                              {a.file_type === 'image' ? '📷' : a.file_type === 'video' ? '🎥' : '📄'} {a.filename}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            )}
          </section>
        )}

        {activeTab === 'volunteer-approvals' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">✅</div>
              <div>
                <h2>Approve Volunteer Engagements</h2>
                <p>Review and approve community volunteer engagement submissions before they become public.</p>
              </div>
            </div>
            
            {loading ? (
              <div className="loading">Loading pending engagements...</div>
            ) : (
              <div className="volunteer-applications">
                {pendingEngagements.length === 0 ? (
                  <div className="empty-state">
                    <p>No pending volunteer engagements to review</p>
                  </div>
                ) : (
                  <div className="applications-list">
                    {pendingEngagements.map(engagement => (
                      <div key={engagement.id} className="application-item engagement-card">
                        <div className="application-header">
                          <div className="applicant-info">
                            <h3>{engagement.title}</h3>
                            <p><strong>Volunteer:</strong> {engagement.volunteer_name} | <strong>Type:</strong> {engagement.engagement_type.replace('_', ' ')}</p>
                          </div>
                          <div className="application-status">
                            <span className="status-badge pending">Pending Review</span>
                            <small>{new Date(engagement.created_at).toLocaleDateString()}</small>
                          </div>
                        </div>
                        
                        <div className="engagement-details">
                          <p><strong>Location:</strong> {engagement.location}</p>
                          <p><strong>Date:</strong> {new Date(engagement.engagement_date).toLocaleString()}</p>
                          <p><strong>Duration:</strong> {engagement.duration_hours} hours</p>
                          <p><strong>Participants:</strong> {engagement.participants_count} community members</p>
                          
                          <div className="motivation-section">
                            <strong>Description:</strong>
                            <p>{engagement.description}</p>
                          </div>
                          
                          {engagement.outcomes && (
                            <div className="skills-section">
                              <strong>Outcomes:</strong>
                              <p>{engagement.outcomes}</p>
                            </div>
                          )}
                          
                          {engagement.attachments_count > 0 && (
                            <div className="experience-section">
                              <strong>Attachments:</strong>
                              <p>{engagement.attachments_count} files uploaded (photos, videos, documents)</p>
                            </div>
                          )}
                        </div>
                        
                        <div className="application-actions">
                          <button 
                            onClick={() => {
                              if (window.confirm('Approve this volunteer engagement? It will become publicly visible.')) {
                                approveVolunteerEngagement(engagement.id, 'approve');
                              }
                            }}
                            className="btn btn-success"
                          >
                            ✓ Approve & Publish
                          </button>
                          <button 
                            onClick={() => {
                              const reason = prompt('Enter rejection reason:');
                              if (reason) {
                                approveVolunteerEngagement(engagement.id, 'reject', reason);
                              }
                            }}
                            className="btn btn-danger"
                          >
                            ✗ Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div className="volunteer-info">
              <h3>Approval Guidelines</h3>
              <p>When reviewing volunteer engagements, verify:</p>
              <ul>
                <li>✓ Activity took place within your ward</li>
                <li>✓ Description is accurate and appropriate</li>
                <li>✓ Attachments are relevant and professional</li>
                <li>✓ Information aligns with ward activities</li>
                <li>✓ No sensitive or inappropriate content</li>
              </ul>
              
              <h4>What Happens After Approval?</h4>
              <ul>
                <li>Engagement becomes publicly visible on the Documents page</li>
                <li>Community members can view the activity and attachments</li>
                <li>Contributes to ward transparency and accountability metrics</li>
                <li>Volunteer receives confirmation of approval</li>
              </ul>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default WardOfficerPage;